import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Col, Container, Row, TopBar } from 'components'
import { useNavigate, useParams } from 'react-router-dom'
import { OutlineCheckIcon } from 'assets'
import './bulkEnrollment.scss'
import { constants, getConnectUser } from 'helpers'
import { setBulkEnrollmentCurrentStep } from 'helpers/bulkEnrollmentAnalytics'
import {
    flattenEnrollmentCatalogToPackages,
    formatBulkEnrollmentPackagePrice
} from 'helpers/bulkEnrollmentHelpers'
import {
    useAppSelector,
    useBindDispatch,
    useBulkEnrollmentAnalytics,
    useScreenSize
} from 'hooks'
import { ZoneInfoTemplate } from 'components/enrollForm/enrollFormTypes'

export const BulkEnrollmentPackageSelection = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { villageId } = useParams()
    const { zoneSFID } = getConnectUser()
    const { trackAbandoned } = useBulkEnrollmentAnalytics(villageId)
    const {
        navbarHandler,
        selectBulkEnrollmentPackageHandler,
        ensureBulkEnrollmentVillageHandler,
        productListHandler
    } = useBindDispatch()
    const { selectedPackage } = useAppSelector((state) => state.bulkEnrollment)
    const { productListData, loading, error } = useAppSelector(
        (state) => state.enrollment
    )
    const screenSize = useScreenSize()
    const isMobile = screenSize.screenWidth < constants.MOBILE_SIZE

    useEffect(() => {
        setBulkEnrollmentCurrentStep('package_selection')
    }, [])

    useEffect(() => {
        if (isMobile) {
            navbarHandler(constants.HIDE_PART_NAVBAR)
        } else {
            navbarHandler(constants.SHOW_NAVBAR)
        }
    }, [isMobile])

    useEffect(() => {
        if (villageId) ensureBulkEnrollmentVillageHandler(villageId)
    }, [villageId])

    useEffect(() => {
        if (zoneSFID) {
            productListHandler({ zoneSFID })
        }
    }, [zoneSFID])

    const packages = useMemo(() => {
        const zones = (productListData || []) as ZoneInfoTemplate[]
        const zoneProducts =
            zones.find((zone) => zone.zone_sf_id === zoneSFID)?.product_list ||
            zones[0]?.product_list ||
            []
        return flattenEnrollmentCatalogToPackages(zoneProducts)
    }, [productListData, zoneSFID])

    return (
        <div className="bulk-enrollment-page page-wrapper">
            <div className="sticky-header" id="sticky-header">
                <Container>
                    <TopBar
                        back
                        backNavigation={() => {
                            trackAbandoned('package_selection')
                            navigate(`/village/${villageId}`)
                        }}
                    >
                        {t('bulkEnrollment.choosePackage')}
                    </TopBar>
                </Container>
            </div>
            <Container>
                <Row>
                    <Col md={12} lg={8}>
                        <p className="bulk-enrollment-subtitle">
                            {t('bulkEnrollment.choosePackageSubtitle')}
                        </p>
                        {loading && (
                            <p className="bulk-enrollment-subtitle">
                                {t('bulkEnrollment.loadingPackages')}
                            </p>
                        )}
                        {!loading && error && (
                            <p className="bulk-enrollment-status bulk-enrollment-status--error">
                                {t('bulkEnrollment.loadPackagesFailed')}
                            </p>
                        )}
                        {!loading && !error && packages.length === 0 && (
                            <p className="bulk-enrollment-subtitle">
                                {t('bulkEnrollment.noPackagesAvailable')}
                            </p>
                        )}
                        {packages.map((item) => {
                            const isSelected =
                                selectedPackage?.package_id === item.package_id
                            return (
                                <button
                                    key={item.package_id}
                                    type="button"
                                    className={`bulk-enrollment-package-card ${
                                        isSelected
                                            ? 'bulk-enrollment-package-card--selected'
                                            : ''
                                    }`}
                                    onClick={() =>
                                        selectBulkEnrollmentPackageHandler(item)
                                    }
                                >
                                    <div className="bulk-enrollment-package-card__image">
                                        <div className="bulk-enrollment-package-card__image-inner">
                                            {item.image_url ? (
                                                <img
                                                    src={item.image_url}
                                                    alt={item.name}
                                                    className="bulk-enrollment-package-card__img"
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className="bulk-enrollment-package-card__content">
                                        <h3 className="bulk-enrollment-title">
                                            {item.name}
                                        </h3>
                                        <div className="bulk-enrollment-package-size-row">
                                            <span className="bulk-enrollment-package-size-chip">
                                                {item.size_display || item.size}
                                            </span>
                                        </div>
                                        <p className="bulk-enrollment-price">
                                            {formatBulkEnrollmentPackagePrice(
                                                item.price,
                                                item.hidePrice
                                            )}
                                        </p>
                                        {isSelected &&
                                            (item.addOns || []).length > 0 && (
                                                <div className="bulk-enrollment-package-card__addons">
                                                    {(item.addOns || []).map(
                                                        (addOn) => (
                                                            <span
                                                                key={
                                                                    addOn.package_id
                                                                }
                                                            >
                                                                {addOn.product_name}
                                                                {addOn.size_display
                                                                    ? ` · ${addOn.size_display}`
                                                                    : ''}
                                                            </span>
                                                        )
                                                    )}
                                                </div>
                                            )}
                                    </div>
                                    <div className="bulk-enrollment-package-card__check">
                                        {isSelected ? (
                                            <span
                                                className="bulk-enrollment-round-check bulk-enrollment-round-check--selected"
                                                aria-hidden="true"
                                            >
                                                <OutlineCheckIcon
                                                    color="#fff"
                                                    width="15"
                                                    height="15"
                                                    strokeWidth="3"
                                                />
                                            </span>
                                        ) : (
                                            <span
                                                className="bulk-enrollment-round-check bulk-enrollment-round-check--empty"
                                                aria-hidden="true"
                                            />
                                        )}
                                    </div>
                                </button>
                            )
                        })}
                    </Col>
                </Row>
            </Container>
            <div className="bulk-enrollment-footer">
                <Button
                    style="primary"
                    disable={!selectedPackage}
                    onClick={() =>
                        navigate(`/village/${villageId}/bulk-enrollment/entry`)
                    }
                    id="bulk-enrollment-package-continue"
                >
                    {t('continue')}
                </Button>
            </div>
        </div>
    )
}
